import React, { useState } from 'react';
import { searchTag } from '../../../actions/keyCodeActions';
import { NavLink } from 'react-router-dom';
import { Table, getVisibleColumns } from '../../common/Table';
import Loading from '../../common/Loading';
import KwPanel from '../../common/KwPanel';
import CodeStatusHistory from '../slideout/CodeStatusHistory';
import AutoSelectText from '../../common/AutoSelectText';
import {
  useTableSettings,
  getTableSettingsDefaults,
} from '../../../hooks/useTableSettings';
import SlidingPane from 'react-sliding-pane';
import CompanyLink from '../../common/links/CompanyLink';
import Button from '../../common/Button';
import { useTranslation } from 'react-i18next';

export interface SearchTagListProps {
  reload?: any;
  tag?: string;
}

function SearchTagList({ reload, tag }: SearchTagListProps) {
  const allColumns = [
    'code',
    'tag1',
    'codeStatus',
    'gameName',
    'skuName',
    'requestReference',
    'companyName',
    'keyPool',
  ];
  const suffixColumns = ['id'];
  const { t } = useTranslation();
  const defaultVisibleColumns = allColumns;

  const [historySlideoutOpen, setHistorySlideoutOpen] = useState(false);
  const [historySlideoutItem, setHistorySlideoutItem] = useState<any>();

  const [codes, setCodes] = useState<any>();
  const { tableSettings, handleSetTableSettings: setTableSettings, filters, filterChange, isLoading } =
    useTableSettings(
      'reporteddicodesearchlistsabledlist-settings',
      getTableSettingsDefaults({
        defaultSort: 'codeStatus',
        direction: 'desc',
        defaultVisibleColumns,
      }),
      {
        tag,
      },
      searchTag,
      setCodes,
      reload,
      t('Failed to search codes')
    );

  const handleShowCodeHistory = (e: React.MouseEvent, code: any) => {
    (e as any).cancelBubble = true;
    setHistorySlideoutItem(code);
    setHistorySlideoutOpen(true);
  };

  let columnsConfig = [
    {
      Key: 'code',
      Label: t('Key'),
      width: '20%',
      className: 'num no-wrap autoselect',
      Render: (item: any) => <AutoSelectText>{item.code}</AutoSelectText>,
    },
    {
      Key: 'tag1',
      Label: t('Tag'),
      width: '10%',
      className: 'num autoselect',
      Render: (item: any) =>
        item.tag1 ? <AutoSelectText>{item.tag1}</AutoSelectText> : '-',
    },
    {
      Key: 'codeStatus',
      Label: t('Key status'),
      width: '10%',
      Render: (item: any) => item.codeStatus,
    },
    {
      Key: 'gameName',
      Label: t('Game'),
      width: '10%',
      Render: (item: any) => (
        <NavLink to={`/game/${item.gameId}`}>{item.gameName}</NavLink>
      ),
    },
    {
      Key: 'skuName',
      Label: t('SKU'),
      width: '10%',
      Render: (item: any) => (
        <NavLink to={`/sku/${item.skuId}`}>{item.skuName}</NavLink>
      ),
    },
    {
      Key: 'requestReference',
      Label: t('Request Ref.'),
      width: '10%',
      Render: (item: any) => item.requestReference ?? '-',
    },
    {
      Key: 'companyName',
      Label: t('Company'),
      width: '10%',
      Render: (item: any) => (
        <>
          <CompanyLink id={item.companyId} name={item.companyName} />
        </>
      ),
    },
    {
      Key: 'keyPool',
      Label: t('Key pool'),
      width: '10%',
      Render: (item: any) => item.keyPool ?? '-',
    },
    {
      Key: 'id',
      Label: '',
      width: '10%',
      Sort: false,
      className: 'text-right',
      Render: (item: any) => (
        <Button
          className="btn btn-default"
          onClick={(e) => handleShowCodeHistory(e, item)}
          text={t('Code history')}
        />
      ),
    },
  ];

  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;
  const suffixColumnsWithConfig = getVisibleColumns(suffixColumns, columnsConfig);
  const columns = getVisibleColumns(visibleColumns, columnsConfig);

  return codes ? (
    <>
      <KwPanel className="mb-4">
        <Table
          columns={columns}
          suffixColumns={suffixColumnsWithConfig}
          allColumns={allColumns}
          rows={codes.data.map((x: any) => {
            return { Key: x.id, ...x };
          })}
          totalRowCount={codes.totalRowCount}
          filteredRowCount={codes.filteredRowCount}
          tableSettings={tableSettings}
          setTableSettings={setTableSettings}
          isLoading={isLoading}
          filterChange={filterChange}
        />
      </KwPanel>

      <SlidingPane
        isOpen={historySlideoutOpen}
        hideHeader={true}
        from="right"
        className="side-panel"
        onRequestClose={() => setHistorySlideoutOpen(false)}
      >
        <>
          {historySlideoutItem && (
            <CodeStatusHistory
              id={historySlideoutItem.id}
              done={() => {
                setHistorySlideoutOpen(false);
              }}
              cancel={() => setHistorySlideoutOpen(false)}
            />
          )}
        </>
      </SlidingPane>
    </>
  ) : (
    <Loading />
  );
}

export default SearchTagList;
